import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import "../styles/TwentyTwentysixContent.css";
import ResourceArticleCard from "../resources/ResourceArticleCard";
import { resourcesArticles } from "../resources/resourcesStubData";
import { loadRssArticles } from "../resources/rssBlogLoader";

const RELATED_COUNT = 3;

export default function ResourceArticlePage() {
  const { slug } = useParams();
  const [articles, setArticles] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadRssArticles()
      .then((data) => {
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) setArticles(data);
        else setArticles(resourcesArticles);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("RSS load failed; falling back to stub data", err);
        if (!cancelled) setArticles(resourcesArticles);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const article = useMemo(() => {
    const list = articles || resourcesArticles;
    return list.find((a) => a.slug === slug) || null;
  }, [articles, slug]);

  const relatedArticles = useMemo(() => {
    const list = articles || resourcesArticles;
    return list
      .filter((a) => a.slug !== slug)
      .slice(0, RELATED_COUNT);
  }, [articles, slug]);

  if (!article && loading) {
    return (
      <div className="twentytwentysix-content">
        <div className="twentytwentysix-content__container">
          <p className="twentytwentysix-content__copy">Loading…</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="twentytwentysix-content">
        <div className="twentytwentysix-content__container">
          <p className="twentytwentysix-content__copy">
            We couldn&apos;t find that article.
          </p>
          <div className="twentytwentysix-content__cta-row">
            <Link to="/twentytwentysix/resources" className="twentytwentysix-content__pill">
              Back to Resources
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const date = article.publishedAt
    ? new Date(article.publishedAt).toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <div className="twentytwentysix-content">
      <div className="twentytwentysix-content__container">
        <section className="twentytwentysix-content__section">
          {article.heroImage && (
            <div
              className="twentytwentysix-content__listing-detail-image-wrap"
              style={{ maxWidth: "640px", marginBottom: 24 }}
            >
              <img
                src={article.heroImage}
                alt={article.title}
                className="twentytwentysix-content__listing-detail-image"
              />
            </div>
          )}

          {article.category && (
            <p className="twentytwentysix-content__listing-detail-meta">
              {article.category}
            </p>
          )}

          <h1 className="twentytwentysix-content__title">{article.title}</h1>

          {(article.author || date) && (
            <p className="twentytwentysix-content__copy--muted">
              {article.author && <span>{article.author}</span>}
              {article.author && date && " · "}
              {date && <span>{date}</span>}
            </p>
          )}

          {Array.isArray(article.body) ? (
            <div className="twentytwentysix-content__listing-detail-body">
              {article.body.map((para, idx) => (
                <p key={idx}>{para}</p>
              ))}
            </div>
          ) : (
            <div
              className="twentytwentysix-content__listing-detail-body"
              dangerouslySetInnerHTML={{ __html: article.bodyHtml || "" }}
            />
          )}

          {relatedArticles.length > 0 && (
            <section className="twentytwentysix-content__section" aria-label="Explore more">
              <h2 className="twentytwentysix-content__subtitle">Explore more</h2>
              <ul className="twentytwentysix-content__list twentytwentysix-content__list--no-bullets twentytwentysix-content__listing-grid">
                {relatedArticles.map((related) => (
                  <ResourceArticleCard key={related.slug} article={related} />
                ))}
              </ul>
            </section>
          )}

          <div className="twentytwentysix-content__cta-row">
            <Link
              to="/twentytwentysix/resources"
              className="twentytwentysix-content__pill twentytwentysix-content__pill--outline"
            >
              Back to Resources
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}

